import { useState, useLayoutEffect } from "react";

const EDGE_PAD = 8;

/**
 * Computes fixed-position coordinates for a portal element anchored to a DOM rect.
 * Handles vertical flipping and horizontal clamping for "top"/"bottom", and
 * side placement with a flip to the opposite side for "left"/"right".
 *
 * @param {DOMRect|null} anchorRect - Bounding rect of the anchor element.
 * @param {React.RefObject} portalRef - Ref attached to the portal element (for measuring).
 * @param {'top'|'bottom'} preferredPlacement - Preferred vertical side to appear on.
 * @param {{ align?: 'center'|'left'|'right', matchAnchorWidth?: boolean, offset?: number, overlapAnchor?: boolean }} options
 *   align "right" lines the panel's right edge up with the anchor's (a panel wider than its trigger
 *   near the right of the screen); "left" lines up the left edges; "center" centres it.
 *   When overlapAnchor is true the panel always drops just below the anchor and overlaps
 *   the content beneath it; it never flips upward even when space below is tight.
 * @returns {{ portalStyle: React.CSSProperties, resolvedPlacement: 'top'|'bottom'|'left'|'right' }}
 */
export const usePortalPosition = (
  anchorRect,
  portalRef,
  preferredPlacement = "bottom",
  options = {},
) => {
  const {
    align = "center",
    matchAnchorWidth = false,
    offset = 8,
    overlapAnchor = false,
  } = options;
  const [resolvedPlacement, setResolvedPlacement] =
    useState(preferredPlacement);
  const [portalStyle, setPortalStyle] = useState({ opacity: 0 });

  useLayoutEffect(() => {
    if (!anchorRect || !portalRef.current) return;

    const pw = portalRef.current.offsetWidth;
    const ph = portalRef.current.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal placements sit BESIDE the anchor and are centred on it
    // vertically. Handled first and returned early: the vertical logic below
    // treats every placement that is not "bottom" as "top", so a "right" would
    // otherwise be silently rendered above the anchor — which is exactly what
    // it did before this branch existed.
    if (preferredPlacement === "right" || preferredPlacement === "left") {
      const fitsRight = anchorRect.right + offset + pw <= vw - EDGE_PAD;
      const fitsLeft = anchorRect.left - offset - pw >= EDGE_PAD;
      // Preferred side, the opposite when it does not fit, and the preferred
      // one again when neither does — a tooltip clipped on the side it was
      // asked for beats one that jumps to the other side and is clipped there.
      let side = preferredPlacement;
      if (preferredPlacement === "right" && !fitsRight && fitsLeft) side = "left";
      if (preferredPlacement === "left" && !fitsLeft && fitsRight) side = "right";

      setResolvedPlacement(side);

      let sideTop = anchorRect.top + anchorRect.height / 2 - ph / 2;
      if (sideTop < EDGE_PAD) sideTop = EDGE_PAD;
      if (sideTop + ph > vh - EDGE_PAD) sideTop = vh - ph - EDGE_PAD;

      const sideLeft =
        side === "right"
          ? anchorRect.right + offset
          : anchorRect.left - offset - pw;

      const sideStyle = { top: sideTop, left: sideLeft, opacity: 1 };
      if (matchAnchorWidth) sideStyle.width = anchorRect.width;
      setPortalStyle(sideStyle);
      return;
    }

    let actualPlacement = preferredPlacement;
    // overlapAnchor keeps the panel anchored to the top edge and never flips up.
    if (!overlapAnchor) {
      if (preferredPlacement === "bottom") {
        if (anchorRect.bottom + offset + ph > vh - EDGE_PAD)
          actualPlacement = "top";
      } else {
        if (anchorRect.top - ph - offset < EDGE_PAD) actualPlacement = "bottom";
      }
    }
    setResolvedPlacement(actualPlacement);

    let top, left;
    if (overlapAnchor) {
      top = anchorRect.bottom + offset;
    } else if (actualPlacement === "bottom") {
      top = anchorRect.bottom + offset;
    } else {
      top = anchorRect.top - ph - offset;
    }

    if (align === "center") {
      left = anchorRect.left + anchorRect.width / 2 - pw / 2;
    } else if (align === "right") {
      left = anchorRect.right - pw;
    } else {
      left = anchorRect.left;
    }

    if (left < EDGE_PAD) left = EDGE_PAD;
    if (left + pw > vw - EDGE_PAD) left = vw - pw - EDGE_PAD;

    const style = { top, left, opacity: 1 };
    if (matchAnchorWidth) style.width = anchorRect.width;
    setPortalStyle(style);
  }, [
    anchorRect,
    preferredPlacement,
    align,
    matchAnchorWidth,
    offset,
    overlapAnchor,
  ]);

  return { portalStyle, resolvedPlacement };
};
